import { useAEMComponentData } from "@AEM/AEMContentLoader"
import { AEMComponentMapper, AEMPageType } from "@AEM/models/AEMPage.model"
import { AssistanceModal } from "@components/ui/AssistanceModal/AssistanceModal"
import { useQAMode } from "@hooks/useQAMode"
import { useRedirect } from "@hooks/useRedirect"
import {
	Alert,
	Box,
	Button,
	Container,
	Flex,
	PasswordInput,
	Stack,
	Text,
	TextInput,
	Title,
	UnstyledButton,
	useMantineTheme
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { LoginError } from "@models/error/login-error"
import {
	IconAlertTriangle,
	IconEye,
	IconEyeOff,
	IconLock
} from "@tabler/icons-react"
import {
	ACCOUNT_LOCKED_ERROR_CODE,
	CREATE_ACCOUNT_LEGACY_APP_ID,
	CREATE_ACCOUNT_LEGACY_URL,
	FORGOT_PASSWORD_LEGACY_APP_ID,
	FORGOT_PASSWORD_LEGACY_URL,
	INVALID_CREDENTIALS_ERROR_CODE,
	INVALID_USERNAME_ERROR_CODE,
	QAMODE_ERROR_CODE,
	VITE_USE_LANGUAGE_PICKER
} from "@utils/constants.util"
import { useIsDesktop } from "@utils/device.utils"
import { useIsMobile } from "@utils/device.utils"
import { stylizeText } from "@utils/stylizeText"
import { FC, useState } from "react"
import { useAuthContext } from "../context/AuthProvider"
import { AEMImage } from "./AEMImage"
import styles from "./ThemedLoginPage.module.css"
import { LanguagePicker, MaintenanceBanner } from "./ui"

const ThemedLoginPage: FC = () => {
	const [username, setUsername] = useState("")
	const [password, setPassword] = useState("")
	const { login, isLoading, error } = useAuthContext()
	const [assistanceModalOpened, assistanceModalHandlers] = useDisclosure(false)
	const { handleRedirect } = useRedirect()
	const isMobile = useIsMobile()
	const theme = useMantineTheme()
	const { isQAMode } = useQAMode()

	const heroComponentData = useAEMComponentData(
		AEMPageType.LOGIN,
		AEMComponentMapper.LOGIN_HERO
	)?.componentData
	const loginFormComponentData = useAEMComponentData(
		AEMPageType.LOGIN,
		AEMComponentMapper.LOGIN_FORM
	)?.componentData

	const handleLogin = async (e: React.FormEvent): Promise<void> => {
		e.preventDefault()
		await login({ username, password })
	}

	const handleAnchorClick = () => {
		handleRedirect({
			link: FORGOT_PASSWORD_LEGACY_URL,
			appId: FORGOT_PASSWORD_LEGACY_APP_ID,
			type: "public"
		})
	}
	const icon =
		error?.errorCode === ACCOUNT_LOCKED_ERROR_CODE ? (
			<IconLock />
		) : (
			<IconAlertTriangle />
		)

	const getErrorMessage = (error: LoginError) => {
		if (error.errorCode === INVALID_USERNAME_ERROR_CODE) {
			return (
				<Text fz={12}>{loginFormComponentData?.unsuccessfulLoginMessage}</Text>
			)
		}

		if (error.errorCode === INVALID_CREDENTIALS_ERROR_CODE) {
			return (
				<>
					<Text fz={12}>
						{loginFormComponentData?.unsuccessfulLoginMessage}
					</Text>
					<Text fz={12} fw={600}>
						{error.remainingAttempts ?? ""}{" "}
						{loginFormComponentData?.attemptsRemaining}
					</Text>
				</>
			)
		}

		if (error.errorCode === ACCOUNT_LOCKED_ERROR_CODE) {
			return (
				<>
					<Text fz={12}>
						{stylizeText(loginFormComponentData?.accountLockedHeader, {
							fz: 12
						})}
					</Text>
					<Text fz={12}>
						{stylizeText(loginFormComponentData?.accountLockedMessage, {
							onAnchorClick: assistanceModalHandlers.open,
							fz: 12
						})}
					</Text>
				</>
			)
		}

		if (error.errorCode === QAMODE_ERROR_CODE) {
			return (
				<Text fz={12}>
					{loginFormComponentData?.qaModeMessage || error.message}
				</Text>
			)
		}

		if (error.errorCode === "INVALID_TOKEN") {
			return <Text fz={12}>{stylizeText(error.message, { fz: 12 })}</Text>
		}

		return `${error.remainingAttempts ?? ""} ${
			loginFormComponentData?.[error?.errorCode] ?? error.message
		}`.trim()
	}

	return (
		<Box
			style={{
				background: `linear-gradient(296deg, rgba(0, 0, 0, 0.00) 4.85%, rgba(0, 0, 0, 0.49) 59.71%), url(${heroComponentData?.image?.path}) lightgray bottom center / cover no-repeat`,
				display: "flex",
				flexDirection: "column",
				overflow: "auto",
				height: "100%"
			}}
			mih={{ base: "100vh", xl: "calc(100vh - 340px)" }}
			className={styles.loginPage}
		>
			<Container size="xl" h="100%" px={0} mx={0}>
				<Flex
					align="center"
					justify="center"
					direction={{ base: "column", md: "row" }}
					gap={{ base: 0, md: 80 }}
					h="100%"
				>
					<Stack
						w={{ base: "100%", sm: 600 }}
						px={{ base: 32, md: 64 }}
						py={{ base: 30.5, md: 100 }}
						h={{ xl: "100%", lg: "100%" }}
						mih={{ base: "100vh", xl: "calc(100vh - 340px)" }}
						className={styles.loginPagePane}
						align="center"
						gap={30}
						style={() => ({
							borderRadius: useIsDesktop() ? 0 : 30
						})}
					>
						<Stack gap="sm" align="center">
							<MaintenanceBanner show={isQAMode} />
							{!isQAMode && (
								<Text
									fz={{ base: 16, md: 18 }}
									fw={500}
									lh={{ base: 0.6875, md: 0.72 }}
									className={styles.text}
								>
									{loginFormComponentData?.welcomeText}
								</Text>
							)}
							<Stack className={styles.logos}>
								<AEMImage
									imageData={loginFormComponentData?.logo}
									w={{ base: 200, xs: 316 }}
									h={{ base: 30, xs: 47 }}
								/>
								<Flex
									direction="row"
									align={"center"}
									justify={"center"}
									gap={{ base: "lg", sm: "xl" }}
								>
									{loginFormComponentData?.additionalLogos?.[0] ? (
										<AEMImage
											w={{ base: 76, sm: 91 }}
											key={`additional-logo-${loginFormComponentData?.additionalLogos?.[0].path}-${0}`}
											imageData={loginFormComponentData?.additionalLogos?.[0]}
										/>
									) : (
										<></>
									)}
									{loginFormComponentData?.additionalLogos?.[1] ? (
										<AEMImage
											w={{ base: 76, sm: 91 }}
											key={`additional-logo-${loginFormComponentData?.additionalLogos?.[1].path}-${1}`}
											imageData={loginFormComponentData?.additionalLogos?.[1]}
										/>
									) : (
										<></>
									)}
									{loginFormComponentData?.additionalLogos?.[2] ? (
										<AEMImage
											w={{ base: 76, sm: 91 }}
											key={`additional-logo-${loginFormComponentData?.additionalLogos?.[2].path}-${2}`}
											imageData={loginFormComponentData?.additionalLogos?.[2]}
										/>
									) : (
										<></>
									)}
								</Flex>
							</Stack>
						</Stack>
						<form onSubmit={handleLogin} className={styles.loginPageForm}>
							<Stack gap="lg">
								<TextInput
									placeholder={loginFormComponentData?.username}
									value={username}
									onChange={(event) => setUsername(event.currentTarget.value)}
									variant="login"
									aria-label={loginFormComponentData?.username}
									classNames={{ input: error ? styles.inputError : "" }}
									error={!!error}
									data-qa="ushome.input.username"
								/>
								<PasswordInput
									placeholder={loginFormComponentData?.password}
									value={password}
									onChange={(event) => setPassword(event.currentTarget.value)}
									variant="login"
									aria-label={loginFormComponentData?.password}
									classNames={{ input: error ? styles.inputError : "" }}
									error={!!error}
									visibilityToggleIcon={({ reveal }) =>
										reveal ? (
											<div data-qa="ushome.button.show.password">
												<IconEye size={20} color={theme.colors.royal[21]} />
											</div>
										) : (
											<IconEyeOff size={20} color={theme.colors.royal[21]} />
										)
									}
									data-qa="ushome.input.password"
								/>
								{error && (
									<Alert variant="filled" color="red" icon={icon} radius={30}>
										{getErrorMessage(error)}
									</Alert>
								)}
								<Button
									className={styles.loginButton}
									type="submit"
									variant="dark-blue"
									size="xl"
									fullWidth
									data-qa="ushome.button.login"
								>
									{isLoading
										? "Loading..."
										: loginFormComponentData?.signInButton}
								</Button>
							</Stack>
						</form>
						<Stack align="center" gap={isMobile ? "xs" : 32}>
							<Stack gap="xs" align="center" mb="md">
								<Text fz={13} className={styles.text}>
									{loginFormComponentData?.createAccountIntroText}
								</Text>
								<Box>
									<Button
										component="a"
										variant="white-glow"
										size="lg"
										onClick={() =>
											handleRedirect({
												link: CREATE_ACCOUNT_LEGACY_URL,
												appId: CREATE_ACCOUNT_LEGACY_APP_ID,
												type: "public"
											})
										}
										data-qa="ushome.button.create.account"
									>
										{loginFormComponentData?.createAccountButtonText}
									</Button>
								</Box>
							</Stack>
							<Stack gap="xs" align="center">
								<Text fz={14} className={styles.text}>
									{stylizeText(loginFormComponentData?.forgotUsernameString, {
										onAnchorClick: handleAnchorClick,
										dataQaAttribute: "ushome.button.forgot",
										fz: 14,
										color: "var(--text-color-primary)"
									})}
								</Text>
								<UnstyledButton
									onClick={assistanceModalHandlers.open}
									fz={14}
									lh={1.6}
									td="underline"
									data-qa="ushome.button.assistance"
								>
									{loginFormComponentData?.needFurtherAssistanceString}
								</UnstyledButton>
								<AssistanceModal
									opened={assistanceModalOpened}
									onClose={assistanceModalHandlers.close}
								/>
							</Stack>
							{/* Santiago Ponce: Business decided to remove the language picker for current version */}
							{VITE_USE_LANGUAGE_PICKER && (
								<LanguagePicker dataQaAttribute="ushome.button.language" />
							)}
						</Stack>
					</Stack>
					<Flex
						align="center"
						justify="center"
						p="xl"
						className={styles.textWhite}
					>
						<Box maw={500} w={{ base: "90%", md: "100%" }}>
							<Title
								fw={600}
								fz={{ base: 36, md: 60 }}
								lh={{ base: 1.2, md: 1.1 }}
								mb={{ base: 10, md: 30 }}
								order={1}
								ta={{ base: "center", md: "left" }}
							>
								{heroComponentData?.title}
							</Title>
							<Text
								fw={600}
								display={{ base: "none", md: "flex" }}
								fz={{ base: 16, md: 20 }}
								lh={1.7}
							>
								{heroComponentData?.backgroundHeroDescription}
							</Text>
						</Box>
					</Flex>
				</Flex>
			</Container>
		</Box>
	)
}
export default ThemedLoginPage
